/**
 * NMS Carousel Scroll Progress Component
 * Provides scroll progress indicators for scroll-carousel elements
 * Automatically calculates base progress based on visible content ratio
 */
class NMSCarouselScrollProgress extends window.NMSBaseClass {
  constructor(element) {
    super();
    this.element = element;
    this.progressId = element.dataset.progressId;
    
    // Store in global registry
    window.neemans.carouselScrollProgress = window.neemans.carouselScrollProgress || {};
    window.neemans.carouselScrollProgress[this.progressId] = this;
  }

  init() {
    super.init();
    this.#setupScrollProgress();
    this.#bindEvents();
  }

  #setupScrollProgress() {
    // Find the carousel and progress bar within this component
    this.carousel = this.element.querySelector('scroll-carousel');
    this.progressBar = this.element.querySelector('[data-ref="progress-bar"]');
    
    if (!this.carousel || !this.progressBar) {
      console.warn('NMSCarouselScrollProgress: Missing carousel or progress bar elements');
      return;
    }

    // Initial progress update
    this.#updateProgress();
  }

  #bindEvents() {
    if (!this.carousel || !this.progressBar) return;

    // Throttled scroll listener for performance
    const throttledUpdate = this.debounce(this.#updateProgress.bind(this), 16); // ~60fps
    
    // Listen to scroll events
    this.carousel.addEventListener('scroll', throttledUpdate);
    
    // Listen to carousel events
    this.carousel.addEventListener('carousel:change', this.#updateProgress.bind(this));
    this.carousel.addEventListener('carousel:settle', this.#updateProgress.bind(this));
    
    // Handle resize
    window.addEventListener('resize', this.debounce(() => {
      this.#updateProgress();
    }, 250));
  }

  #updateProgress() {
    if (!this.carousel || !this.progressBar) return;

    const scrollLeft = this.carousel.scrollLeft;
    const scrollWidth = this.carousel.scrollWidth - this.carousel.clientWidth;
    const carouselWidth = this.carousel.clientWidth;
    const totalWidth = this.carousel.scrollWidth;
    
    // Calculate how much content is initially visible (base progress)
    const initialVisibleRatio = carouselWidth / totalWidth;
    const baseProgress = initialVisibleRatio * 100;
    
    // Calculate scroll progress for the remaining content
    const remainingProgress = 100 - baseProgress;
    const scrollProgress = scrollWidth > 0 ? (scrollLeft / scrollWidth) * remainingProgress : 0;
    
    // Total progress = base + scroll progress
    const totalProgress = baseProgress + scrollProgress;
    
    // Update progress bar width
    const finalProgress = Math.min(Math.max(totalProgress, baseProgress), 100);
    this.progressBar.style.width = finalProgress + '%';

    // Dispatch custom event for external listeners
    this.dispatchNMSEvent('progress-update', {
      progress: finalProgress,
      baseProgress,
      scrollProgress,
      scrollLeft,
      scrollWidth
    });
  }

  // Public API methods
  updateProgress() {
    this.#updateProgress();
  }

  getCurrentProgress() {
    if (!this.carousel) return 0;
    
    const scrollLeft = this.carousel.scrollLeft;
    const scrollWidth = this.carousel.scrollWidth - this.carousel.clientWidth;
    const carouselWidth = this.carousel.clientWidth;
    const totalWidth = this.carousel.scrollWidth;
    
    const initialVisibleRatio = carouselWidth / totalWidth;
    const baseProgress = initialVisibleRatio * 100;
    const remainingProgress = 100 - baseProgress;
    const scrollProgress = scrollWidth > 0 ? (scrollLeft / scrollWidth) * remainingProgress : 0;
    
    return Math.min(Math.max(baseProgress + scrollProgress, baseProgress), 100);
  }

  destroy() {
    super.destroy();
    // Clean up global reference
    if (window.neemans.carouselScrollProgress?.[this.progressId]) {
      delete window.neemans.carouselScrollProgress[this.progressId];
    }
  }
}

// Initialize component when DOM is ready
function initNMSCarouselScrollProgress() {
  if (!window.NMSBaseClass) {
    console.error('NMSCarouselScrollProgress: NMSBaseClass required');
    return;
  }
  
  const elements = document.querySelectorAll('[data-nms-carousel-scroll-progress]');
  
  for (const element of elements) {
    if (!element.hasAttribute('data-initialized')) {
      const instance = new NMSCarouselScrollProgress(element);
      instance.init();
      element.setAttribute('data-initialized', 'true');
    }
  }
}

// Auto-initialize
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initNMSCarouselScrollProgress);
} else {
  initNMSCarouselScrollProgress();
}

// Re-initialize when sections are added dynamically (Shopify theme editor)
if (typeof Shopify !== 'undefined' && Shopify.designMode) {
  document.addEventListener('shopify:section:load', initNMSCarouselScrollProgress);
}